import { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, AlertCircle, Loader2 } from "lucide-react";
import { API } from "../constants";

// Landing page for the "CONTINUE TO SETUP" button in the manual-approval
// email. URL shape: /verify/approved?token=vt_xxxxxxxxxxxxxxxxxxxx
//
// Flow:
//   1. Read token from ?token= — refuse if missing or malformed.
//   2. POST /api/verify/lookup-token — backend validates (approved, unused,
//      unexpired) and returns sector + email + org_name.
//   3. Redirect to /{sector}/signup?token=vt_xxx — the existing
//      OrgVerification component already has an effect that picks up the
//      ?token=... query string, calls onVerified(), and unlocks the
//      Membership Tier + setup fields with the manager email locked to the
//      verified organisation email.
//   4. If the token is invalid / expired / already consumed / not approved,
//      show a friendly error page — NEVER fall through to the family home.
export default function VerifyApprovedLanding() {
  const [state, setState] = useState({ phase: "checking", error: "" });

  useEffect(() => {
    const usp = new URLSearchParams(window.location.search);
    const token = usp.get("token") || "";
    if (!token) {
      setState({ phase: "error", error: "This link is missing its approval token. Please open the exact link from the approval email." });
      return;
    }
    if (!/^vt_[A-Za-z0-9_-]{20,}$/.test(token)) {
      setState({ phase: "error", error: "This approval link isn't in the expected format. Please open the exact link from your approval email." });
      return;
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/verify/lookup-token`, { verify_token: token });
        const { sector, email } = r.data || {};
        if (!["hotel", "school", "charity"].includes(sector)) {
          setState({ phase: "error", error: "This approval doesn't belong to a recognised organisation type. Please contact us." });
          return;
        }
        // All good — bounce to the sector's setup form with the token AND
        // the verified email preserved so OrgVerification skips straight to
        // 'verified' and HotelSignUp locks the manager-email field.
        const qs = new URLSearchParams({ token });
        if (email) qs.set("email", email);
        window.location.replace(`/${sector}/signup?${qs.toString()}`);
      } catch (e) {
        const msg = e?.response?.data?.detail || "We couldn't validate this approval link. It may have expired or already been used.";
        setState({ phase: "error", error: msg });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4" data-testid="verify-approved-landing">
      <div className="max-w-md w-full bg-white rounded-2xl border-2 border-[#E8E5DC] p-6 sm:p-8 text-center">
        {state.phase === "checking" && (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-[#B89823]" />
            <h1 className="text-lg sm:text-xl font-bold text-[#4A4A4A] mb-1">Confirming your approval…</h1>
            <p className="text-sm text-[#6B6B6B]">One moment while we open your setup form.</p>
          </>
        )}
        {state.phase === "error" && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto mb-3 text-[#A55B64]" />
            <h1 className="text-lg sm:text-xl font-bold text-[#4A4A4A] mb-2" data-testid="verify-approved-error-title">This approval link can't be opened</h1>
            <p className="text-sm text-[#6B6B6B] mb-5" data-testid="verify-approved-error-message">{state.error}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <a href="/organisations" data-testid="verify-approved-go-organisations" className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#4A4A4A] text-white text-sm font-semibold hover:bg-black">
                <ShieldCheck className="w-4 h-4" /> Start a new verification
              </a>
              <a href="mailto:shop@poodlesandfriends.uk" className="inline-flex items-center justify-center px-4 py-2 rounded-full border-2 border-[#E8E5DC] text-sm font-semibold text-[#4A4A4A] hover:bg-[#F3F0E6]">
                Contact us
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
